import { Button, Group, Modal, Stack, Text, useMantineTheme } from '@mantine/core'
import { IconPlus, IconShoppingCart } from '@tabler/icons'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'

import { LoginSignupModalForm } from '@/containers/forms'
import { useStudent } from '@/lib/hooks'
import {
  api,
  errorHandler,
  getProductInfo,
  getSession,
  isValidCPF,
  notificationHandler,
  productOptions,
  removeSession,
  setSession
} from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import { useBasket } from '@/providers/BasketProvider'
import { useNav } from '@/providers/NavProvider'
import {
  BasketProductOption as BasketProductOptionType,
  Price as PriceType,
  Product as ProductType,
  ProductOption as ProductOptionType,
  Showcase as ShowcaseType,
  Site as SiteType,
  Store as StoreType,
  StudentChannelProfile as StudentChannelProfileType
} from '@/types'

interface Props {
  siteData?: SiteType
  store?: StoreType
  showcase?: ShowcaseType
  product: ProductType
  selectedOptions?: any
  addToCart: boolean
  addToCartLabel?: string
  addToCartProps?: any
  buyNow: boolean
  buyNowLabel?: string
  buyNowProps?: any
  continueBuying?: boolean
  handleClosePaymentDrawer?: () => void
  handleCloseView?: () => void
}

export default function BuyButton({
  siteData,
  store,
  showcase,
  product,
  selectedOptions = [],
  addToCart = true,
  addToCartLabel,
  addToCartProps = {},
  buyNow = true,
  buyNowLabel,
  buyNowProps = {},
  continueBuying = true,
  handleClosePaymentDrawer,
  handleCloseView
}: Props) {
  // Hooks
  const router = useRouter()
  const theme = useMantineTheme()
  const { isAuthenticated } = useAuth() || {}
  const { basket, basketMutate, setOpenAddedProductModal, setOpenBasketDrawer } = useBasket()
  const { setStudentModalOpenFromBuyButton } = useNav()
  const { studentData } = useStudent()

  // Constants
  const {
    site: siteSlug,
    channel: channelSlug,
    store: storeSlug,
    showcase: showcaseSlug,
    collection: collectionSlug
  } = router.query || {}

  const { clean } = productOptions || {}
  const { uid: productUid, hasOptions, hasKit, isKitBundler } = product || {}
  const { type, price } = getProductInfo(product) || {}
  const isKit = Boolean(hasKit || isKitBundler || type === 'kit')
  const shouldValidateOptions = Boolean(hasOptions && !isKit)
  const hideSignUp = !siteData?.isSignupAllowed || siteData?.privacy === 'private'

  const addButtonRef = useRef(null)
  const buyButtonRef = useRef(null)

  const continueBuyingUrl = `/${channelSlug}/${storeSlug}/${showcaseSlug}${
    collectionSlug ? `?collection=${collectionSlug}` : ''
  }`

  const sessionStudentUid = getSession('stuid')

  const currentChannelStudentChannelProfile = studentData?.studentChannelProfiles?.find(
    (studentChannelProfile: StudentChannelProfileType) =>
      studentChannelProfile?.channelProfile?.channel?.slug === channelSlug
  )

  // States
  const [loading, setLoading] = useState(null)
  const [openedModal, setOpenedModal] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)

  // Options validation
  const requiredOptions =
    shouldValidateOptions && product?.options
      ? product.options
        .filter((option: ProductOptionType) => option.isRequired)
        .map((option: ProductOptionType) => option.code)
      : []

  const canAddToCart = (() => {
    if (!shouldValidateOptions || !product?.options) return true

    const allRequiredFilled =
      requiredOptions.length === 0 ||
      (Boolean(selectedOptions) &&
        requiredOptions.every((code: string) => {
          const value = selectedOptions[code]
          if (value === undefined || value === null || value === '') return false
          if (Array.isArray(value)) return value.length > 0
          return true
        }))

    if (!allRequiredFilled) return false

    if (selectedOptions) {
      for (const option of product.options) {
        const optionValue = selectedOptions[option.code]
        const hasCpfValidator = option.validators?.some(
          validator => validator.type === 'cpf'
        )
        if (hasCpfValidator && optionValue && !isValidCPF(optionValue)) {
          return false
        }
      }
    }

    return true
  })()

  // Actions
  const handleQuantity = (price?: PriceType) => {
    if (price?.minimalSellingQuantity) {
      return price?.minimalSellingQuantity
    }
    return 1
  }

  const addProductToBasket = useCallback(
    async (productUid: string, options: BasketProductOptionType[], price?: PriceType) => {
      const quantity = handleQuantity(price)
      const response = await api.post(`/${siteSlug}/basket/add/`, {
        product: productUid,
        ...(siteData?.beneficiary !== 'shopper' ? { student: sessionStudentUid } : {}),
        showcase: showcase?.uid,
        quantity,
        options: isKit ? [] : clean(options, product?.options)
      })
      return response
    },
    [showcase?.uid, siteSlug, sessionStudentUid, siteData?.beneficiary, clean, isKit, product?.options]
  )

  const onAddConfirm = useCallback(
    async (buttonRef: any) => {
      setLoading(buttonRef)
      if (isAuthenticated) {
        if (sessionStudentUid || siteData?.beneficiary === 'shopper') {
          removeSession('buttonRef')
          removeSession('productUid')
          try {
            await addProductToBasket(productUid, selectedOptions, price)
            if (buttonRef === addButtonRef || buttonRef === 'add') {
              basketMutate?.()
              setOpenAddedProductModal(product)
              if (continueBuying) router.push(continueBuyingUrl)
            } else {
              if (basket?.uid) router.push(`/checkout/${basket.uid}`)
              else setOpenBasketDrawer?.(true)
            }
            removeSession('modalAction')
            setConfirmModal(null)
            handleClosePaymentDrawer?.()
            handleCloseView?.()
          } catch (error: any) {
            const errors = errorHandler(error?.response?.data)
            notificationHandler({
              variant: 'error',
              message:
                errors?.messages?.[0] || 'Ocorreu um erro ao adicionar o produto no carrinho.'
            })
          }
        } else {
          setSession('buttonRef', buttonRef === addButtonRef ? 'add' : 'buy')
          setSession('productUid', productUid)
          setStudentModalOpenFromBuyButton?.(true)
        }
      } else {
        // Show login/register popup
        setSession('buttonRef', buttonRef === addButtonRef ? 'add' : 'buy')
        setSession('productUid', productUid)
        setOpenedModal(true)
      }
      setLoading(null)
    },
    [
      isAuthenticated,
      sessionStudentUid,
      siteData?.beneficiary,
      addProductToBasket,
      productUid,
      selectedOptions,
      price,
      handleClosePaymentDrawer,
      handleCloseView,
      basketMutate,
      setOpenAddedProductModal,
      product,
      continueBuying,
      router,
      continueBuyingUrl,
      basket?.uid,
      setOpenBasketDrawer,
      setStudentModalOpenFromBuyButton
    ]
  )

  const handleAdd = useCallback(
    (buttonRef: any) => {
      const shouldConfirmStudentEnrollmentChange =
        !!channelSlug &&
        !!showcaseSlug &&
        !!studentData &&
        !!currentChannelStudentChannelProfile &&
        !!currentChannelStudentChannelProfile?.showcase &&
        !!currentChannelStudentChannelProfile.showcase?.uid &&
        currentChannelStudentChannelProfile.showcase.uid !== showcase?.uid

      if (shouldConfirmStudentEnrollmentChange) setConfirmModal(buttonRef)
      else onAddConfirm(buttonRef)
    },
    [
      channelSlug,
      showcaseSlug,
      showcase?.uid,
      studentData,
      currentChannelStudentChannelProfile,
      onAddConfirm
    ]
  )

  const handleAddOrBuy = useCallback(async () => {
    const sessionButtonRef = getSession('buttonRef')
    const sessionProductUid = getSession('productUid')
    if (sessionButtonRef && sessionProductUid === productUid) {
      await handleAdd(sessionButtonRef)
    }
  }, [handleAdd, productUid])

  const handleCloseConfirmModal = () => {
    setConfirmModal(null)
    removeSession('buttonRef')
    removeSession('productUid')
  }

  // Effects
  useEffect(() => {
    async function runModalFallback() {
      const sessionModalAction = getSession('modalAction')
      if (sessionModalAction) {
        if (!sessionStudentUid && siteData?.beneficiary !== 'shopper') {
          setStudentModalOpenFromBuyButton?.(true)
        } else if (siteData?.beneficiary === 'shopper') {
          await handleAddOrBuy()
        }
      }
    }
    runModalFallback()
  }, [handleAddOrBuy, setStudentModalOpenFromBuyButton, siteData?.beneficiary, sessionStudentUid])

  useEffect(() => {
    async function runFallbackFunction() {
      if (sessionStudentUid) {
        await handleAddOrBuy()
      }
    }
    runFallbackFunction()
  }, [sessionStudentUid, handleAddOrBuy])

  return (
    <>
      <Stack spacing="xs" sx={{ flex: 1 }}>
        {!!addToCart && (
          <Button
            loading={loading === addButtonRef}
            color="green"
            onClick={() => handleAdd(addButtonRef)}
            disabled={!canAddToCart}
            leftIcon={<IconPlus />}
            fullWidth
            ref={addButtonRef}
            {...addToCartProps}>
            {addToCartLabel || 'Adicionar ao carrinho'}
          </Button>
        )}
        {!!buyNow && (
          <Button
            fullWidth
            color="green"
            loading={loading === buyButtonRef}
            onClick={() => handleAdd(buyButtonRef)}
            disabled={!canAddToCart}
            leftIcon={<IconShoppingCart />}
            ref={buyButtonRef}
            {...buyNowProps}>
            {buyNowLabel || 'Comprar agora'}
          </Button>
        )}
      </Stack>
      <Modal
        centered
        size="auto"
        title={<Text weight="bold">Confirmar alteração de vitrine?</Text>}
        opened={!!confirmModal}
        onClose={handleCloseConfirmModal}>
        <Text mb="xs">
          Você está tentando adicionar um produto da vitrine{' '}
          <em>
            <strong>
              {store?.grade?.parent?.name}: {store?.displayName} - {showcase?.displayName} (
              {showcase?.cycle})
            </strong>
          </em>{' '}
          para o aluno{' '}
          <strong>
            {studentData?.firstName} {studentData?.lastName}
          </strong>
          .
        </Text>
        <Text mb="xs">
          No entanto, o aluno{' '}
          <strong>
            {studentData?.firstName} {studentData?.lastName}
          </strong>{' '}
          já está matriculado em{' '}
          <em>
            <strong>
              {currentChannelStudentChannelProfile?.showcase?.store?.grade?.parent?.name}:{' '}
              {currentChannelStudentChannelProfile?.showcase?.store?.displayName} -{' '}
              {currentChannelStudentChannelProfile?.showcase?.displayName} (
              {currentChannelStudentChannelProfile?.showcase?.cycle})
            </strong>
          </em>
          .
        </Text>
        <Text mb="xl">
          Deseja atualizar a matrícula de{' '}
          <strong>
            {studentData?.firstName} {studentData?.lastName}
          </strong>{' '}
          para{' '}
          <em>
            <strong>
              {store?.grade?.parent?.name}: {store?.displayName} - {showcase?.displayName} (
              {showcase?.cycle})
            </strong>
          </em>
          ?
        </Text>
        <Group position="apart">
          <Button color="red" disabled={loading === confirmModal} onClick={handleCloseConfirmModal}>
            Cancelar
          </Button>
          <Button
            color={theme.colors[theme.primaryColor][7]}
            loading={loading === confirmModal}
            onClick={() => onAddConfirm(confirmModal)}>
            Confirmar
          </Button>
        </Group>
      </Modal>
      <LoginSignupModalForm
        setOpened={setOpenedModal}
        opened={openedModal}
        hideSignUp={hideSignUp}
      />
    </>
  )
}
